/**
 * API Error Handling System
 * Provides comprehensive error categorization and handling
 */

import { ApiError, ApiErrorCode } from '@/types/api';

// Error Action Interface
export interface ErrorAction {
  label: string;
  action: () => void | Promise<void>;
  primary?: boolean;
}

// App Error Interface
export interface AppError {
  id: string;
  code: ApiErrorCode;
  message: string;
  userMessage: string;
  context?: string;
  timestamp: Date;
  retryable: boolean;
  actions?: ErrorAction[];
  provider?: string;
  originalError?: unknown;
}

// Error Recovery Strategy Interface
export interface ErrorRecoveryStrategy {
  canRecover(error: AppError): boolean;
  recover(error: AppError): Promise<void>;
  getRecoveryActions(error: AppError): ErrorAction[];
}

/**
 * Creates a standardized API error
 */
export function createApiError(
  code: ApiErrorCode,
  message: string,
  options: {
    retryable?: boolean;
    retryAfter?: number;
    provider?: string;
    originalError?: unknown;
  } = {}
): ApiError {
  const error = new Error(message) as ApiError;
  error.code = code;
  error.retryable = options.retryable ?? isRetryableError(code);
  error.retryAfter = options.retryAfter;
  error.provider = options.provider;
  error.originalError = options.originalError;
  
  return error;
}

/**
 * Determines if an error code is retryable
 */
export function isRetryableError(code: ApiErrorCode): boolean {
  const retryableErrors: ApiErrorCode[] = [
    'NETWORK_ERROR',
    'TIMEOUT_ERROR',
    'RATE_LIMIT_ERROR',
    'SERVER_ERROR'
  ];
  
  return retryableErrors.includes(code);
}

/**
 * Categorizes errors from HTTP responses
 */
export function categorizeHttpError(status: number, message?: string): ApiErrorCode {
  if (status >= 500) {
    return 'SERVER_ERROR';
  }
  
  switch (status) {
    case 401:
    case 403:
      return 'AUTH_ERROR';
    case 404:
      return 'NOT_FOUND_ERROR';
    case 429:
      return 'RATE_LIMIT_ERROR';
    case 400:
      return 'VALIDATION_ERROR';
    default:
      return 'UNKNOWN_ERROR';
  }
}

/**
 * Extracts retry-after header value
 */
export function extractRetryAfter(headers: Record<string, string>): number | undefined {
  const retryAfter = headers['retry-after'] || headers['Retry-After'];
  if (retryAfter) {
    const seconds = parseInt(retryAfter, 10);
    return isNaN(seconds) ? undefined : seconds * 1000; // Convert to milliseconds
  }
  return undefined;
}

/**
 * Network Error Recovery Strategy
 */
export class NetworkErrorRecovery implements ErrorRecoveryStrategy {
  canRecover(error: AppError): boolean {
    return error.code === 'NETWORK_ERROR' || error.code === 'TIMEOUT_ERROR';
  }
  
  async recover(error: AppError): Promise<void> {
    // Implement retry logic with exponential backoff
    // This would be handled by the API service's retry mechanism
    console.log(`Attempting to recover from ${error.code}:`, error.message);
  }
  
  getRecoveryActions(error: AppError): ErrorAction[] {
    return [
      {
        label: 'Retry',
        action: () => this.recover(error),
        primary: true
      },
      {
        label: 'Check Connection',
        action: () => this.checkConnection()
      }
    ];
  }
  
  private async checkConnection(): Promise<void> {
    // Implement connection check
    try {
      await fetch('https://httpbin.org/status/200', { 
        method: 'HEAD',
        signal: AbortSignal.timeout(5000)
      });
      console.log('Network connection is available');
    } catch {
      console.log('Network connection is not available');
    }
  }
}

/**
 * Rate Limit Error Recovery Strategy
 */
export class RateLimitErrorRecovery implements ErrorRecoveryStrategy {
  canRecover(error: AppError): boolean {
    return error.code === 'RATE_LIMIT_ERROR';
  }
  
  async recover(error: AppError): Promise<void> {
    const retryAfter = this.getRetryDelay(error);
    console.log(`Rate limited. Waiting ${retryAfter}ms before retry`);
    await new Promise(resolve => setTimeout(resolve, retryAfter));
  }
  
  getRecoveryActions(error: AppError): ErrorAction[] {
    const retryAfter = this.getRetryDelay(error);
    
    return [
      {
        label: `Wait ${Math.ceil(retryAfter / 1000)}s and Retry`,
        action: () => this.recover(error),
        primary: true
      },
      {
        label: 'Switch Provider',
        action: () => this.switchProvider()
      }
    ];
  }
  
  private getRetryDelay(error: AppError): number {
    // Use retry-after if available, otherwise default to 60 seconds
    return (error as any).retryAfter || 60000;
  }
  
  private switchProvider(): void {
    console.log('Switching to alternative API provider');
    // This would be handled by the API service
  }
}

/**
 * Authentication Error Recovery Strategy
 */
export class AuthErrorRecovery implements ErrorRecoveryStrategy {
  canRecover(error: AppError): boolean {
    return error.code === 'AUTH_ERROR';
  }
  
  async recover(error: AppError): Promise<void> {
    console.log('Authentication error - user intervention required');
  }
  
  getRecoveryActions(error: AppError): ErrorAction[] {
    return [
      {
        label: 'Check API Key',
        action: () => this.openSettings(),
        primary: true
      },
      {
        label: 'Use Demo Mode',
        action: () => this.useDemoMode()
      }
    ];
  }
  
  private openSettings(): void {
    console.log('Opening settings to check API key');
    // This would trigger opening the settings panel
  }
  
  private useDemoMode(): void {
    console.log('Switching to demo mode');
    // This would switch to demo/free tier mode
  }
}

/**
 * Error Handler Class
 */
export class ErrorHandler {
  private recoveryStrategies: ErrorRecoveryStrategy[] = [
    new NetworkErrorRecovery(),
    new RateLimitErrorRecovery(),
    new AuthErrorRecovery()
  ];
  
  private errors: Map<string, AppError> = new Map();
  private errorListeners: ((error: AppError) => void)[] = [];
  
  /**
   * Add error listener for external integrations (like stores)
   */
  addErrorListener(listener: (error: AppError) => void): () => void {
    this.errorListeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.errorListeners.indexOf(listener);
      if (index > -1) {
        this.errorListeners.splice(index, 1);
      }
    };
  }
  
  /**
   * Notify all error listeners
   */
  private notifyErrorListeners(error: AppError): void {
    this.errorListeners.forEach(listener => {
      try {
        listener(error);
      } catch (err) {
        console.error('Error in error listener:', err);
      }
    });
  }
  
  /**
   * Handle an error and convert it to AppError
   */
  handleError(error: unknown, context?: string): AppError {
    const appError = this.convertToAppError(error, context);
    this.errors.set(appError.id, appError);
    
    // Log error for debugging
    console.error(`[${appError.code}] ${appError.message}`, {
      context: appError.context,
      provider: appError.provider,
      originalError: appError.originalError
    });
    
    // Notify listeners
    this.notifyErrorListeners(appError);
    
    return appError;
  }
  
  /**
   * Show error to user with recovery options
   */
  showError(message: string, options: {
    code?: ApiErrorCode;
    context?: string;
    retryable?: boolean;
  } = {}): AppError {
    const appError: AppError = {
      id: this.generateErrorId(),
      code: options.code || 'UNKNOWN_ERROR',
      message,
      userMessage: this.getUserFriendlyMessage(options.code || 'UNKNOWN_ERROR', message),
      context: options.context,
      timestamp: new Date(),
      retryable: options.retryable ?? isRetryableError(options.code || 'UNKNOWN_ERROR'),
      actions: this.getRecoveryActions({
        id: '',
        code: options.code || 'UNKNOWN_ERROR',
        message,
        userMessage: '',
        timestamp: new Date(),
        retryable: false
      })
    };
    
    this.errors.set(appError.id, appError);
    
    // Notify listeners
    this.notifyErrorListeners(appError);
    
    return appError;
  }
  
  /**
   * Clear errors by context or all errors
   */
  clearErrors(context?: string): void {
    if (context) {
      for (const [id, error] of this.errors.entries()) {
        if (error.context === context) {
          this.errors.delete(id);
        }
      }
    } else {
      this.errors.clear();
    }
  }
  
  /**
   * Get errors by context or all errors
   */
  getErrors(context?: string): AppError[] {
    const errors = Array.from(this.errors.values());
    return context 
      ? errors.filter(error => error.context === context)
      : errors;
  }
  
  /**
   * Convert unknown error to AppError
   */
  private convertToAppError(error: unknown, context?: string): AppError {
    let code: ApiErrorCode = 'UNKNOWN_ERROR';
    let message = 'An unknown error occurred';
    let provider: string | undefined;
    
    if (error instanceof Error) {
      message = error.message;
      
      // Check if it's already an ApiError
      if ('code' in error) {
        code = (error as ApiError).code;
        provider = (error as ApiError).provider;
      }
      // Check for network errors
      else if (error.name === 'TypeError' && message.includes('fetch')) {
        code = 'NETWORK_ERROR';
      }
      // Check for timeout errors
      else if (error.name === 'AbortError' || message.includes('timeout')) {
        code = 'TIMEOUT_ERROR';
      }
    }
    
    return {
      id: this.generateErrorId(),
      code,
      message,
      userMessage: this.getUserFriendlyMessage(code, message),
      context,
      timestamp: new Date(),
      retryable: isRetryableError(code),
      actions: this.getRecoveryActions({
        id: '',
        code,
        message,
        userMessage: '',
        timestamp: new Date(),
        retryable: false,
        provider
      }),
      provider,
      originalError: error
    };
  }
  
  /**
   * Get user-friendly error message
   */
  private getUserFriendlyMessage(code: ApiErrorCode, originalMessage: string): string {
    const messages: Record<ApiErrorCode, string> = {
      'NETWORK_ERROR': 'Unable to connect to the server. Please check your internet connection.',
      'TIMEOUT_ERROR': 'The request timed out. Please try again.',
      'RATE_LIMIT_ERROR': 'Too many requests. Please wait a moment before trying again.',
      'AUTH_ERROR': 'Authentication failed. Please check your API key.',
      'VALIDATION_ERROR': 'Invalid request. Please check your input.',
      'NOT_FOUND_ERROR': 'The requested resource was not found.',
      'SERVER_ERROR': 'Server error. Please try again later.',
      'UNKNOWN_ERROR': 'An unexpected error occurred. Please try again.'
    };
    
    return messages[code] || originalMessage;
  }
  
  /**
   * Get recovery actions for an error
   */
  private getRecoveryActions(error: AppError): ErrorAction[] {
    for (const strategy of this.recoveryStrategies) {
      if (strategy.canRecover(error)) {
        return strategy.getRecoveryActions(error);
      }
    }
    
    // Default recovery actions
    return error.retryable ? [
      {
        label: 'Retry',
        action: () => console.log('Retrying...'),
        primary: true
      }
    ] : [];
  }
  
  /**
   * Generate unique error ID
   */
  private generateErrorId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Export singleton instance
export const errorHandler = new ErrorHandler();